import {TradeAllocationDto, TradeChainDto, TradeRecordDto} from '../../../../models/generated-models';

/**
 * One row of the journey tree on the ห่วงโซ่ tab: a record, how much of it came from the row
 * above (the allocation line), and the rows that drew from it in turn.
 */
export interface ChainTreeNode {
  record: TradeRecordDto;
  /** Quantity drawn from the parent row into this record (kg); undefined on a root. */
  quantity?: number;
  children: ChainTreeNode[];
  /** An export record's ด่าน, the last stop of the chain. */
  checkpointName?: string;
  /** The record being viewed. */
  current: boolean;
}

/** Map pin numbers by record id: the record's own pin, and the ด่าน pin of an export record. */
export interface ChainTreePins {
  records: Map<number, number>;
  checkpoints: Map<number, number>;
}

/** A journey never nests deeper than this; also the guard against odd data. */
const MAX_TREE_DEPTH = 25;

/**
 * The whole journey as one tree in flow order: every origin lot is a root, under it whoever drew
 * from it, and so on down to the exporter and its ด่าน. A record that pooled several lots hangs
 * under each of them (once per lot, with what it took from that lot), which is how a merge reads
 * in a tree. A record the chain payload does not carry (older responses) is shown from the line's
 * own denormalised fields, so nothing on a line is ever dropped.
 */
export function buildJourneyTree(chain: TradeChainDto | null | undefined): ChainTreeNode[] {
  const current = chain?.record;
  if (!chain || !current || current.id == null) {
    return [];
  }
  const records = new Map<number, TradeRecordDto>();
  for (const item of [
    ...(chain.ancestors ?? []),
    current,
    ...(chain.descendants ?? chain.children ?? []),
    ...(chain.related ?? [])
  ]) {
    if (item.id != null && !records.has(item.id)) {
      records.set(item.id, item);
    }
  }
  const lines: TradeAllocationDto[] = (chain.links ?? [
    // Older payloads carry only the record's own lines and the ones out of it.
    ...(current.sources ?? []),
    ...(chain.outgoing ?? [])
  ]).filter((line) => line.sourceRecordId != null && line.recordId != null);

  for (const line of lines) {
    if (!records.has(line.sourceRecordId!)) {
      records.set(line.sourceRecordId!, {
        id: line.sourceRecordId, kind: line.sourceKind, businessTitle: line.sourceBusinessTitle,
        productName: line.sourceProductName, lotCode: line.sourceLotCode, recordedAt: line.sourceRecordedAt
      });
    }
    if (!records.has(line.recordId!)) {
      records.set(line.recordId!, {
        id: line.recordId, kind: line.recordKind, businessTitle: line.recordBusinessTitle, recordedAt: line.recordRecordedAt
      });
    }
  }

  const node = (record: TradeRecordDto, quantity: number | undefined, path: Set<number>, depth: number): ChainTreeNode => ({
    record,
    quantity,
    current: record.id === current.id,
    checkpointName: record.export === true ? (record.exportCheckpointName || 'ด่านที่ส่งออก') : undefined,
    children: depth >= MAX_TREE_DEPTH ? [] : lines
      .filter((line) => line.sourceRecordId === record.id && !path.has(line.recordId!))
      .map((line) => node(records.get(line.recordId!)!, line.quantity, new Set(path).add(line.recordId!), depth + 1))
  });

  // Roots: records nothing on the journey feeds. A lone record is its own root.
  const fed = new Set(lines.map((line) => line.recordId!));
  const roots = [...records.values()].filter((record) => record.id != null && !fed.has(record.id));
  return roots.map((record) => node(record, undefined, new Set([record.id!]), 0));
}
